<template>
  <div class="tab-content">
    <div class="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div>
        <h2 class="text-2xl font-bold text-gray-900 mb-2 flex items-center">
          <font-awesome-icon icon="sitemap" class="mr-3 text-blue-600" />
          Departments
        </h2>
        <p class="text-gray-500">Manage program departments across institutions</p>
      </div>
      <div class="flex space-x-4">
        <select v-model="selectedEntity"
          class="px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white">
          <option value="">All Institutions</option>
          <option v-for="institution in institutions" :key="institution.id" :value="institution.entity">
            {{ getDisplayEntityCode(institution.entity) }}
          </option>
        </select>
        <base-button @click="showModal = true"
          class="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 px-6 py-3 rounded-lg font-semibold">
          <font-awesome-icon icon="plus" class="mr-2" />
          Add Department
        </base-button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-12">
      <base-page-spinner type="spin" class-list="h-12"></base-page-spinner>
      <p class="text-gray-500 mt-4">Loading departments...</p>
    </div>

    <!-- Departments List -->
    <div v-else class="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
      <table class="min-w-full divide-y divide-gray-200">
        <thead class="bg-gray-50">
          <tr>
            <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Institution</th>
            <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Campus</th>
            <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Department Code
            </th>
            <th class="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Department Name
            </th>
            <th class="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
            <th class="px-6 py-4 text-center text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody class="bg-white divide-y divide-gray-100">
          <tr v-for="department in filteredDepartments" :key="department.id"
            class="hover:bg-blue-50 transition-colors duration-150">
            <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
              {{ getDisplayEntityCode(department.entity) }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
              {{ department.campus || '-' }}
            </td>
            <td class="px-6 py-4 whitespace-nowrap">
              <span
                class="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-blue-100 text-blue-800">
                {{ department.prg_dpt }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ department.name }}</td>
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <span :class="department.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'"
                class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium">
                {{ department.is_active ? 'Active' : 'Inactive' }}
              </span>
            </td>
            <td class="px-6 py-4 whitespace-nowrap text-center">
              <div class="flex items-center justify-center space-x-3">
                <button @click="editDepartment(department)"
                  class="text-blue-600 hover:text-blue-800 hover:bg-blue-100 p-2 rounded-lg transition-all duration-200">
                  <font-awesome-icon icon="edit" />
                </button>
                <button @click="deleteDepartment(department.id)"
                  class="text-red-600 hover:text-red-800 hover:bg-red-100 p-2 rounded-lg transition-all duration-200">
                  <font-awesome-icon icon="trash" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      <!-- Empty State -->
      <div v-if="filteredDepartments.length === 0" class="text-center py-12 bg-gray-50">
        <font-awesome-icon icon="sitemap" class="text-4xl text-gray-400 mb-4" />
        <p class="text-gray-500">No departments found</p>
      </div>
    </div>

    <!-- Department Modal -->
    <base-modal v-if="showModal" :title="form.id ? 'Edit Department' : 'Add Department'" @close="closeModal">
      <div class="p-8">
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <font-awesome-icon icon="building" class="mr-2 text-blue-600" />
            Institution
          </label>
          <select v-model="form.entity"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
            :disabled="!!form.id" @change="onEntityChange">
            <option value="">Select Institution</option>
            <option v-for="institution in institutions" :key="institution.id" :value="institution.entity">
              {{ getDisplayEntityCode(institution.entity) }}
            </option>
          </select>
        </div>
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <font-awesome-icon icon="map-marker-alt" class="mr-2 text-blue-600" />
            Campus
          </label>
          <select v-model="form.campus"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
            :disabled="!!form.id || !form.entity">
            <option value="">Select Campus</option>
            <option v-for="campus in filteredCampusesForForm" :key="campus.name" :value="campus.name">
              {{ campus.name }}
            </option>
          </select>
        </div>
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <font-awesome-icon icon="hashtag" class="mr-2 text-blue-600" />
            Department Code
          </label>
          <input v-model="form.prg_dpt" type="text" placeholder="e.g., NRS, BEH"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white"
            :disabled="!!form.id" />
        </div>
        <div class="mb-6">
          <label class="block text-sm font-semibold text-gray-700 mb-3 flex items-center">
            <font-awesome-icon icon="sitemap" class="mr-2 text-blue-600" />
            Department Name
          </label>
          <input v-model="form.name" type="text" placeholder="e.g., Nursing"
            class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all duration-200 bg-white" />
        </div>
        <div class="mb-6">
          <label class="flex items-center space-x-3 cursor-pointer">
            <input v-model="form.is_active" type="checkbox"
              class="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
            <span class="text-sm font-semibold text-gray-700">Active</span>
          </label>
        </div>
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <base-button @click="closeModal"
            class="bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-xl font-semibold transition-all duration-200">
            Cancel
          </base-button>
          <base-button @click="form.id ? updateDepartment() : saveDepartment()"
            class="bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 px-6 py-3 rounded-lg font-semibold">
            {{ form.id ? 'Update' : 'Save' }}
          </base-button>
        </div>
      </div>
    </base-modal>
  </div>
</template>

<script>
import { gql } from 'graphql-tag';
import { getDisplayEntityCode, getEntityOrderIndex } from '../utils';

export default {
  name: 'DepartmentsTab',
  props: {
    institutions: {
      type: Array,
      default: () => []
    },
    allCampuses: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      allDepartments: [],
      selectedEntity: '',
      showModal: false,
      form: {
        id: null,
        entity: '',
        campus: '',
        prg_dpt: '',
        name: '',
        is_active: true
      },
      loadingData: false
    };
  },
  computed: {
    filteredCampusesForForm() {
      if (!this.form.entity || !this.allCampuses || !Array.isArray(this.allCampuses)) {
        return [];
      }
      return this.allCampuses.filter(c => c.entity === this.form.entity);
    },
    filteredDepartments() {
      if (!this.allDepartments || !Array.isArray(this.allDepartments)) {
        return [];
      }

      let departments = [...this.allDepartments];

      if (this.selectedEntity) {
        departments = departments.filter(d => d && d.entity === this.selectedEntity);
      }

      return departments.sort((a, b) => {
        if (!a || !b) return 0;

        const indexA = getEntityOrderIndex(a.entity);
        const indexB = getEntityOrderIndex(b.entity);

        if (indexA !== indexB) {
          return indexA - indexB;
        }

        const campusA = (a.campus && String(a.campus).trim()) || '';
        const campusB = (b.campus && String(b.campus).trim()) || '';
        const campusCompare = campusA.localeCompare(campusB);
        if (campusCompare !== 0) {
          return campusCompare;
        }

        const codeA = a.prg_dpt || '';
        const codeB = b.prg_dpt || '';
        return codeA.localeCompare(codeB);
      });
    }
  },
  methods: {
    getDisplayEntityCode,
    async loadDepartments() {
      this.loadingData = true;
      try {
        const response = await this.$apollo.query({
          query: gql`
            query GetProgramDepartments($entity: String, $is_active: Boolean) {
              programDepartments(entity: $entity, is_active: $is_active) {
                error
                error_code
                message
                data {
                  id
                  entity
                  campus
                  prg_dpt
                  name
                  is_active
                  created_at
                  updated_at
                }
              }
            }
          `,
          variables: {
            entity: null,
            is_active: null
          },
          fetchPolicy: 'network-only'
        });

        if (response.data && response.data.programDepartments && !response.data.programDepartments.error) {
          this.allDepartments = response.data.programDepartments.data || [];
        } else {
          this.allDepartments = [];
        }
      } catch (error) {
        console.error('Failed to load departments:', error);
        this.allDepartments = [];
      } finally {
        this.loadingData = false;
      }
    },
    closeModal() {
      this.showModal = false;
      this.form = {
        id: null,
        entity: '',
        campus: '',
        prg_dpt: '',
        name: '',
        is_active: true
      };
    },
    onEntityChange() {
      this.form.campus = '';
    },
    async saveDepartment() {
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation CreateProgramDepartment($input: CreateProgramDepartmentInput!) {
              createProgramDepartment(input: $input) {
                error
                error_code
                message
                data {
                  id
                  entity
                  campus
                  prg_dpt
                  name
                  is_active
                }
              }
            }
          `,
          variables: {
            input: {
              entity: this.form.entity,
              campus: this.form.campus || null,
              prg_dpt: this.form.prg_dpt,
              name: this.form.name,
              is_active: this.form.is_active !== undefined ? this.form.is_active : true
            }
          }
        });

        if (response.data.createProgramDepartment.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.createProgramDepartment.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Department created successfully.'
        });

        this.closeModal();
        await this.$nextTick();
        this.loadDepartments();
        this.$emit('refresh', 'departments');
      } catch (error) {
        console.error('Failed to save department:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to save department. Please try again.'
        });
      }
    },
    async updateDepartment() {
      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation UpdateProgramDepartment($input: UpdateProgramDepartmentInput!) {
              updateProgramDepartment(input: $input) {
                error
                error_code
                message
                data {
                  id
                  entity
                  campus
                  prg_dpt
                  name
                  is_active
                }
              }
            }
          `,
          variables: {
            input: {
              id: this.form.id,
              campus: this.form.campus || null,
              name: this.form.name,
              is_active: this.form.is_active
            }
          }
        });

        if (response.data.updateProgramDepartment.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.updateProgramDepartment.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Department updated successfully.'
        });

        this.closeModal();
        await this.$nextTick();
        this.loadDepartments();
        this.$emit('refresh', 'departments');
      } catch (error) {
        console.error('Failed to update department:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to update department. Please try again.'
        });
      }
    },
    editDepartment(department) {
      this.form = {
        id: department.id,
        entity: department.entity,
        campus: department.campus || '',
        prg_dpt: department.prg_dpt,
        name: department.name,
        is_active: department.is_active
      };
      this.showModal = true;
    },
    async deleteDepartment(id) {
      const result = await this.$swal.fire({
        title: 'Are you sure?',
        text: 'This will permanently delete the department.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (!result.isConfirmed) {
        return;
      }

      try {
        const response = await this.$apollo.mutate({
          mutation: gql`
            mutation DeleteProgramDepartment($id: ID!) {
              deleteProgramDepartment(id: $id) {
                error
                error_code
                message
              }
            }
          `,
          variables: {
            id: id
          }
        });

        if (response.data.deleteProgramDepartment.error) {
          this.$swal.fire({
            icon: 'error',
            title: 'Error',
            text: response.data.deleteProgramDepartment.message
          });
          return;
        }

        const Toast = this.$swal.mixin({
          toast: true,
          position: 'top-end',
          showConfirmButton: false,
          timer: 3000,
          timerProgressBar: true,
          didOpen: (toast) => {
            toast.addEventListener('mouseenter', this.$swal.stopTimer);
            toast.addEventListener('mouseleave', this.$swal.resumeTimer);
          }
        });
        Toast.fire({
          icon: 'success',
          title: 'Department has been deleted.'
        });

        await this.$nextTick();
        this.loadDepartments();
        this.$emit('refresh', 'departments');
      } catch (error) {
        console.error('Failed to delete department:', error);
        this.$swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete department. Please try again.'
        });
      }
    }
  },
  mounted() {
    this.loadDepartments();
  }
};
</script>
